import React, { useState, useEffect, useCallback } from 'react';
import { adminService, api } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Badge } from '../../components/UI/Badge';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Pagination } from '../../components/UI/Pagination';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  Receipt,
  Search,
  Trash2,
  ExternalLink,
} from 'lucide-react';

export const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { success, error: toastError } = useToast();

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.invoices({ search, page });
      if (res && res.data) {
        setInvoices(res.data.data || []);
        setPagination({
          currentPage: res.data.current_page,
          lastPage: res.data.last_page,
          total: res.data.total,
          from: res.data.from,
          to: res.data.to,
        });
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar facturas globales.');
    } finally {
      setLoading(false);
    }
  }, [search, page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInvoices();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchInvoices]);

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    setDeleting(true);
    try {
      await adminService.deleteInvoice(invoiceToDelete.id);
      success('Factura eliminada del sistema.');
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
      fetchInvoices();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar factura.');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (Number(status)) {
      case 1:
        return <Badge variant="success" size="sm" dot>Pagada</Badge>;
      case 2:
        return <Badge variant="default" size="sm">Cerrada</Badge>;
      default:
        return <Badge variant="warning" size="sm" dot>Abierta</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
          <Receipt className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
          Facturas Globales del Sistema
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
          Visor y control de todas las facturas emitidas por los usuarios de la plataforma
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="w-full sm:w-80">
            <Input
              placeholder="Buscar por factura, cliente o usuario..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando facturas globales..." className="py-12" />
          ) : invoices.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No hay facturas registradas"
              description="No se encontraron registros de facturación global."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Número</th>
                    <th className="py-3 px-4">Emisor (Usuario)</th>
                    <th className="py-3 px-4">Cliente</th>
                    <th className="py-3 px-4">Fecha</th>
                    <th className="py-3 px-4">Total</th>
                    <th className="py-3 px-4 text-center">Estado</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {invoices.map((inv) => (
                    <tr
                      key={inv.id}
                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-900 dark:text-slate-100">
                        #{inv.number || String(inv.id).padStart(6, '0')}
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-800 dark:text-slate-200">
                          {inv.user?.name || '—'}
                        </div>
                        <div className="text-[11px] text-slate-400">{inv.user?.email}</div>
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-800 dark:text-slate-200">
                          {inv.customer?.full_name || '—'}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                        {inv.invo_date}
                      </td>

                      <td className="py-3.5 px-4 font-black text-slate-900 dark:text-slate-100">
                        {inv.currency?.symbol || '$'} {Number(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        {getStatusBadge(inv.status)}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={ExternalLink}
                            onClick={() => window.open(api.getPdfUrl('invoices', inv.id), '_blank')}
                            title="Ver PDF"
                          />

                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setInvoiceToDelete(inv);
                              setDeleteConfirmOpen(true);
                            }}
                            title="Eliminar del sistema"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar factura global?"
        message={`¿Estás seguro de que deseas eliminar la factura #${invoiceToDelete?.number} de la base de datos?`}
        confirmText="Eliminar"
        isLoading={deleting}
      />
    </div>
  );
};
